<template>
  <ResponsiveLayout>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <CorporateHeader />

      <BaseBanner
        :description="$t('generic_sign_up_eligible_sign_up_banner_subtitle')"
        :title="$t('generic_sign_up_eligible_sign_up_banner_title')"
        scroll-into-view
      />

      <div class="kds-content-block">
        <p
          class="kds-typography-display-small"
          v-html="$t('generic_sign_up_with_kaia')"
        />
        <p class="kds-typography-paragraph-large">
          {{ $t('generic_sign_up_personal_info_text') }}
        </p>
      </div>

      <BaseBanner
        v-if="showLoginBanner"
        scroll-into-view
        variant="important"
      >
        <p
          class="kds-typography-paragraph-large"
          v-html="
            $t('b2b__verification__warning_description_with_support_email', {
              VUE_APP_SUPPORT_EMAIL: env.VITE_SUPPORT_EMAIL,
            })
          "
        />
        <template #actions>
          <BaseButton
            data-qa="login"
            @click="router.push({ name: ROUTE.LOGIN })"
          >
            {{ $t('generic_login_with_existing_account') }}
          </BaseButton>
        </template>
      </BaseBanner>

      <BaseBanner
        v-if="showErrorBanner"
        :title="$t('generic__error_title')"
        scroll-into-view
        variant="warning"
      >
        <div
          v-html="
            $t('generic__error_message', {
              email: env.VITE_SUPPORT_EMAIL,
            })
          "
        />
      </BaseBanner>

      <form
        id="signup_form"
        class="kds-content-block"
      >
        <TextField
          v-if="showFirstName"
          :label="$t('b2b_sign_up_name_label')"
          autocomplete="given-name"
          data-qa="first-name"
          v-bind="fieldAttrs.firstName"
        />

        <TextField
          v-if="showLastName"
          :label="$t('b2b_sign_up_last_name_label')"
          autocomplete="family-name"
          data-qa="last-name"
          v-bind="fieldAttrs.lastName"
        />

        <DateOfBirthField
          v-if="showDateOfBirth"
          v-bind="fieldAttrs.dateOfBirth"
        />

        <PhonenumberField
          :country="country"
          :label="$t('b2b_sign_up_phone_number_label')"
          v-bind="fieldAttrs.phoneNumber"
        />

        <!-- just a space -->
        <div />

        <TextField
          :label="$t('generic_field_email')"
          autocomplete="email"
          data-qa="email"
          type="email"
          v-bind="fieldAttrs.email"
        />

        <PasswordField
          autocomplete="new-password"
          data-qa="password"
          show-password-requirements
          v-bind="fieldAttrs.password"
        />

        <BaseBanner variant="info">
          <CheckboxField
            data-qa="accept-terms-and-conditions"
            v-bind="fieldAttrs.acceptTerms"
          >
            <DocumentViewer :link="$t('b2b__new_account__accept_terms')" />
          </CheckboxField>
        </BaseBanner>
      </form>
    </div>
    <template #actions>
      <BaseButton
        data-qa="submit"
        form="signup_form"
        v-bind="submitAttrs"
      >
        {{ $t('generic_create_account') }}
      </BaseButton>
      <AlreadyHaveAccount />
    </template>
    <template #footer><FooterLinks v-if="!isFromApp()" /></template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import {
  useUsStore,
  getEligibilitySessionHash,
} from '@us/app/stores/useUsStore.js'
import usePersistedConfigStore from '@shared/stores/usePersistedConfigStore.js'
import { useRoute, useRouter } from 'vue-router'

import {
  getTimezone,
  isDoBKey,
  isEmailKey,
  isFirstNameKey,
  isLastNameKey,
  setPageTitle,
} from '@shared/utils'
import ROUTE from '@us/app/router/appModule/names'
import Tracker from '@shared/Tracker'
import { useI18n } from 'petite-vue-i18n'

import useForm from '@shared/composables/useForm.js'

import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'

import CorporateHeader from '@shared/components/CorporateHeader.vue'
import AlreadyHaveAccount from '@us/app/components/AlreadyHaveAccount.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import CheckboxField from '@shared/components/form/CheckboxField.vue'
import DateOfBirthField from '@shared/components/form/DateOfBirthField.vue'
import PasswordField from '@shared/components/form/PasswordField.vue'
import PhonenumberField from '@shared/components/form/PhonenumberField.vue'
import TextField from '@shared/components/form/TextField.vue'
import DocumentViewer from '@shared/components/DocumentViewer.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import env from '@shared/env.js'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import HeaderHelp from '@us/app/components/HeaderHelp.vue'
import * as apiRequest from '@shared/api-endpoints'
import useSharedStore from '@shared/stores/useSharedStore.js'

const userStore = useUserStore()
const corporateStore = useCorporateStore()
const usStore = useUsStore()
const sharedStore = useSharedStore()
const persistedConfigStore = usePersistedConfigStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const lang = computed(() => userStore.lang)
const country = computed(() => {
  return userStore.country || env.VITE_DEFAULT_COUNTRY_CODE
})
const formData = computed(() => usStore.formData)
const corporateEligibilityData = computed(
  () => usStore.corporateEligibilityData,
)

const corporate = computed(() => corporateStore.corporate)

const showLoginBanner = ref(false)
const showErrorBanner = ref(false)

const kaiaSubmissionTrackEvent = (success, errorMessage) => {
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.create_account',
      action: 'create',
      object_type: 'account',
      screen_name: 'onb_account_creation',
    },
    {
      search_success: success,
      error_message: errorMessage,
      ...sharedStore.utmData,
    },
  )
}

// searches the corporateEligibilityData and initialQuery for an entry
// where the entries key matches the keyIdentifier condition and returns the
// entries value (null otherwise), used to prefill the form with known data.
const getFromKnownData = (keyIdentifier) =>
  [
    ...Object.entries(corporateEligibilityData.value),
    ...Object.entries(sharedStore.initialQuery),
  ].find(([key]) => keyIdentifier(key))?.[1]

const redirectToNext = async () => {
  await router.push({
    name: ROUTE.SUBSCRIPTION,
  })
}

const form = ref({
  firstName: formData.value.firstName || getFromKnownData(isFirstNameKey) || '',
  lastName: formData.value.lastName || getFromKnownData(isLastNameKey) || '',
  dateOfBirth: formData.value.dateOfBirth || getFromKnownData(isDoBKey) || '',
  phoneNumber: formData.value.phoneNumber || '',
  email: formData.value.email || getFromKnownData(isEmailKey) || '',
  password: formData.value.password || '',
  acceptTerms: formData.value.acceptTerms || false,
})

const showFirstName = ref(!form.value.firstName)
const showLastName = ref(!form.value.lastName)
const showDateOfBirth = ref(!form.value.dateOfBirth)

watchEffect(() => {
  usStore.updateFormData({ ...form.value })
})

const formConfig = computed(() => {
  const config = {}

  if (showFirstName.value) config.firstName = { validations: ['required'] }
  if (showLastName.value) config.lastName = { validations: ['required'] }
  if (showDateOfBirth.value) config.dateOfBirth = { validations: ['dob'] }
  config.phoneNumber = { validations: ['required', 'phone'] }
  config.email = { validations: ['required', 'email'] }
  config.password = { validations: ['password'] }
  config.acceptTerms = { validations: ['accepted'] }

  return config
})

const { fieldAttrs, submitAttrs } = useForm(form, formConfig, {
  onSubmit: async () => {
    showLoginBanner.value = false
    showErrorBanner.value = false

    const emailCode = usStore.voucher?.emailCode
    const lookupKey =
      corporateEligibilityData.value?.lookupKey || route.query.lookupKey || ''

    await apiRequest.signUp({
      ...form.value,
      phoneNumber: form.value.phoneNumber.split(' ').join(''),
      corporateId: corporate.value.id,
      language: lang.value,
      country: country.value,
      timezone: getTimezone(),
      verificationEmail: emailCode,
      landingPageIncentive: sharedStore.initialQuery.utm_campaign?.includes(
        'landing-incentive-mat',
      )
        ? 'mat'
        : null,
      lookupKey,
      // Directly true for US users, because they don't have to accept the terms separately
      acceptedDataProtection: true,
      acceptedTracking: persistedConfigStore.performanceTrackingEnabled,
      eligibilitySessionHash: getEligibilitySessionHash(),
      fromApp: isFromApp(),
      corporateEligibilityData: corporateEligibilityData.value,
      referrer: sharedStore.initialQuery.referrer,
      signupContext: usStore.signupContext,
    })
  },
  onSuccess: async () => {
    kaiaSubmissionTrackEvent(true)
    await redirectToNext()
  },
  onError: (error) => {
    kaiaSubmissionTrackEvent(false, error)

    const status = error?.response?.status
    switch (status) {
      case 403:
        showLoginBanner.value = true
        break
      default:
        showErrorBanner.value = true
        console.error(error)
    }
  },
})

onMounted(() => {
  setPageTitle(t('b2b__signup__create_account__page_title'))

  Object.keys(formConfig.value).forEach((fieldKey) => {
    if (route.query[fieldKey]) {
      form.value[fieldKey] = route.query[fieldKey]
    }
  })
})
</script>

<style lang="scss" scoped>
// TODO: Overwrite password requirements ordering as in the old layout the horizontal space is not enough
:deep(.password-input__password-requirements) {
  @include grid-container(1fr 1fr, auto, xs);

  @include media-up(xxl) {
    @include grid-container(repeat(4, 1fr), auto, md);
  }
}
</style>
